/**
 * The admin console's one adapter failure (ADR-20260925 rule 1). Every HTTP
 * adapter (src/lib/api-client.ts, src/api/client.ts, src/shared/api/client.ts)
 * throws it: the shared ApiFailure from @agion/ui/api-failure (kind, status,
 * code, data, retryable) plus the server's reason as statusText. Its message
 * is a diagnostic for logs; next pages word a failure with failureReason() and
 * branch on isApiFailure(error) and error.status / error.code.
 *
 * Kept in its own module so a test that mocks an adapter module still gets
 * the real failure type.
 */
import { NetworkError } from 'ky'
import { ApiFailure, type ApiFailureKind } from '@agion/ui/api-failure'

export class UnifiedApiError extends ApiFailure {
  /** The server's reason (its `message` or `error`), else the HTTP status text. */
  readonly statusText: string

  constructor(status: number, statusText: string, data?: unknown, code?: string, kind: ApiFailureKind = 'http') {
    super(kind === 'http' ? `API Error ${status}: ${statusText}` : statusText, { kind, status, data, code })
    this.name = 'UnifiedApiError'
    this.statusText = statusText
  }

  /** The server's own retry verdict (`retryable` in its body), when it gave one. */
  get serverRetryable(): boolean | undefined {
    const data = this.data
    if (typeof data !== 'object' || data === null || !('retryable' in data)) return undefined
    const retryable = (data as { retryable?: unknown }).retryable
    return typeof retryable === 'boolean' ? retryable : undefined
  }
}

/** Let Ky classify and retry native network errors before the final conversion. */
export async function adapterFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  return globalThis.fetch(input, init)
}

/** Preserve the public adapter failure after Ky has exhausted its retries. */
export function adapterError(error: Error): Error {
  return error instanceof NetworkError
    ? new UnifiedApiError(0, 'Network error: no response from the platform', undefined, undefined, 'network')
    : error
}
